import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
/** import third-party libraries in the section above, Sort Lines Ascending */

import {
    CTV_DEVICE_OS_OPTIONS,
    DOOH_DEVICES_OS_OPTIONS,
} from 'packages/forms/ad-form/services/default-values';
/** import multi application imports in the section above, Sort Lines Ascending */

import { CampaignTypeMapping } from 'states/resources/campaigns/business-logic';
import { getAdPresetPropertyDisplayName } from 'services/resources/ad-preset';
import { processBidStrategyValue } from 'forms/ad-form/modules/bid-strategy';
/** import single application imports in the section above, Sort Lines Ascending */

/**
 * Fields from ad presets that are restricted on certain platforms.
 * Blocklists include exclude counterparts (`carriers_exclude`, `target_device_language_exclude`) where the platform blocks the matching include field.
 */
export const AdPresetPlatformRestrictedFields = {
    ctv: [
        'geoboxes',
        'target_age_groups',
        'target_device_language',
        'target_device_language_exclude',
        'target_genders',
        'target_dooh_publishers',
        'target_dooh_publishers_exclude',
    ],
    dooh: [
        'target_age_groups',
        'target_carriers',
        'carriers_exclude',
        'target_device_language',
        'target_device_language_exclude',
        'target_device_os',
        'target_genders',
    ],
    inapp: ['target_dooh_publishers', 'target_dooh_publishers_exclude'],
    mweb: ['target_dooh_publishers', 'target_dooh_publishers_exclude'],
    desktop: ['target_dooh_publishers', 'target_dooh_publishers_exclude'],
};

/**
 * Draft keys presets must not apply on guaranteed inventory (hidden on the PG ad form).
 * Geotargets are allowed and stripped to countries via allowSpecifyLocations.
 */
export const AdPresetGuaranteedInventoryBlockedFields = [
    'geoboxes',
    'target_device_os',
    'target_carriers',
    'carriers_exclude',
    'target_device_language',
    'target_device_language_exclude',
    'target_genders',
    'target_age_groups',
    'max_cpm_rate_local',
    'target_dooh_publishers',
    'target_dooh_publishers_exclude',
];

/**
 * Preset draft fields gated by the same env capabilities that hide ad-form controls.
 * Exclude counterparts share the include capability.
 */
const AD_PRESET_ENV_GATED_FIELDS = {
    target_genders: 'canUseGenderTargeting',
    target_age_groups: 'canUseAgeTargeting',
    geoboxes: 'canUseGeoFencing',
    target_carriers: 'canUseCarrierIsp',
    carriers_exclude: 'canUseCarrierIsp',
    target_dooh_publishers: 'canUseCTVOrDOOH',
    target_dooh_publishers_exclude: 'canUseCTVOrDOOH',
};

/**
 * Whether the field is allowed by the injected environment capabilities.
 * Callers must pass `options.environmentSettings` for env-gated fields.
 *
 * @param {string} field
 * @param {Object} [options]
 * @param {Object} [options.environmentSettings] - Required when field is env-gated
 * @returns {boolean}
 */
export function isAdPresetFieldAllowedByEnvironment(field, options = {}) {
    const capabilityName = AD_PRESET_ENV_GATED_FIELDS[field];
    if (!capabilityName) {
        return true;
    }

    if (options.environmentSettings === undefined || options.environmentSettings === null) {
        throw new Error(
            `options.environmentSettings is required for env-gated ad preset field "${field}"`
        );
    }

    const capability = options.environmentSettings[capabilityName];
    if (typeof capability !== 'function') {
        return true;
    }

    return capability() === true;
}

/**
 * Checked properties from all ad_preset_properties groups.
 *
 * @param {Object} adPreset
 * @returns {Object[]}
 */
function getCheckedAdPresetProperties(adPreset) {
    return (adPreset.ad_preset_properties || []).flatMap(group =>
        (group.properties || []).filter(prop => prop.checked === true)
    );
}

/**
 * Draft field keys for a property (`fields` when present, otherwise `[key]`).
 *
 * @param {Object} prop
 * @returns {string[]}
 */
function getAdPresetPropertyFields(prop) {
    return prop.fields && prop.fields.length > 0 ? prop.fields : [prop.key];
}

/**
 * Determines if the given platforms might have restrictions on ad presets.
 *
 * @param {*} platforms - The platforms to check for potential restrictions.
 * @returns {boolean} - Returns true if the platforms could have restrictions, otherwise false.
 */
export function couldAdPresetHaveRestrictions(platforms) {
    const platformsWithRestriction = Object.keys(AdPresetPlatformRestrictedFields);
    return platforms.some(platform => platformsWithRestriction.includes(platform));
}

/**
 * Check if the given ad preset field can be used for the platforms (and optionally PG / env / bid mode).
 *
 * @param {*} platforms - An array of platforms to check against.
 * @param {*} field - The ad preset field to verify.
 * @param {Object} [options]
 * @param {boolean} [options.isGuaranteedInventory] - When true, also reject PG form-blocked fields
 * @param {string} [options.bidStrategyMode] - Ad draft bid mode; when set, Total Bid only for `fixed_bid_price`
 * @param {Object} [options.environmentSettings] - Required for env-gated fields (callers pass getEnvironmentSettings())
 * @returns {boolean} - Returns true if the field can be used, otherwise false.
 */
export function canAdPresetFieldBeUsed(platforms, field, options = {}) {
    const isCTVAd = platforms.includes(CampaignTypeMapping.CTV);
    const isDOOHAd = platforms.includes(CampaignTypeMapping.DOOH);

    let allowedByPlatform;
    if (isCTVAd) {
        allowedByPlatform = !AdPresetPlatformRestrictedFields.ctv.includes(field);
    } else if (isDOOHAd) {
        allowedByPlatform = !AdPresetPlatformRestrictedFields.dooh.includes(field);
    } else {
        allowedByPlatform =
            !AdPresetPlatformRestrictedFields.inapp.includes(field) &&
            !AdPresetPlatformRestrictedFields.mweb.includes(field) &&
            !AdPresetPlatformRestrictedFields.desktop.includes(field);
    }

    if (!allowedByPlatform) {
        return false;
    }

    // PG form hides these targeting/inventory/bid fields — do not apply them from presets
    if (options.isGuaranteedInventory && AdPresetGuaranteedInventoryBlockedFields.includes(field)) {
        return false;
    }

    // Env capabilities that hide ad-form controls also reject those preset fields on apply
    if (!isAdPresetFieldAllowedByEnvironment(field, options)) {
        return false;
    }

    // Total Bid Price only applies when the ad is already Fixed bid; omit option = no bid-mode gate
    if (
        field === 'max_cpm_rate_local' &&
        options.bidStrategyMode !== undefined &&
        options.bidStrategyMode !== 'fixed_bid_price'
    ) {
        return false;
    }

    return true;
}

/**
 * Field keys from checked preset properties that pass platform/PG/env/bid-mode gates.
 * Shared by SET_AD_PRESET merge and applied-property labels.
 *
 * @param {string[]} platforms
 * @param {Object} adPreset
 * @param {Object} [options]
 * @param {boolean} [options.isGuaranteedInventory]
 * @param {string} [options.bidStrategyMode]
 * @param {Object} [options.environmentSettings] - Required when any checked field is env-gated
 * @returns {string[]}
 */
export function getAdPresetEnabledFields(platforms, adPreset, options = {}) {
    return getCheckedAdPresetProperties(adPreset).flatMap(prop =>
        getAdPresetPropertyFields(prop).filter(field =>
            canAdPresetFieldBeUsed(platforms, field, options)
        )
    );
}

/**
 * An array that combines the device OS options for both DOOH and CTV platforms.
 * This constant is used to filter out the target_device_os values that are not applicable
 * to the DOOH and CTV ad platforms.
 * @constant
 * @type {string[]}
 */
const DOOH_AND_CTV_DEVICE_OS_OPTIONS = [...DOOH_DEVICES_OS_OPTIONS, ...CTV_DEVICE_OS_OPTIONS];

/**
 * Adjusts the `target_device_os` field in the ad preset data to match the platform's device OS options
 * or removes it if it's empty after filtering
 *
 * @param {string[]} platforms - An array of platforms to check against, which can include ctv and dooh.
 * @param {Object} adPreset - The ad preset data object containing various ad configuration fields.
 * @param {string[]} enabledFields - An array of fields that are enabled and should be considered for filtering.
 * @param {Object} [options]
 * @param {boolean} [options.allowSpecifyLocations] - Same as Geotargets prop; when false, clear include/exclude so only countries apply
 * @returns {Object} The modified ad preset data object with `target_device_os` field filtered according to the platform.
 *
 * @example
 * const platforms = ['ctv'];
 * const adPreset = {
 *   target_device_os: ['Android', 'iOS', 'Roku'],
 *   other_field: 'value1'
 * };
 * const enabledFields = ['target_device_os', 'other_field'];
 * // result will have `target_device_os` filtered to only include 'Roku' if 'Roku' is in CTV_DEVICE_OS_OPTIONS.
 */
export function getAdPresetValuesToApply(platforms, adPreset, enabledFields, options = {}) {
    const isCTVAd = platforms.includes(CampaignTypeMapping.CTV);
    const isDOOHAd = platforms.includes(CampaignTypeMapping.DOOH);

    const presetData = pick(adPreset, enabledFields);
    const { target_device_os: originalTargetOS } = presetData;

    /**
     * @param {Object} data - Preset fields picked for apply
     * @returns {Object}
     */
    function finalizePresetData(data) {
        // Match Geotargets allowSpecifyLocations={false}: country-only — drop city/region/FSA from preset
        if (options.allowSpecifyLocations === false && Array.isArray(data.geotargets)) {
            data.geotargets = data.geotargets.map(gt => ({
                country: gt.country,
                include: [],
                exclude: [],
            }));
        }

        // Total Bid applied only after gates (incl. fixed bid) — keep automatic_bid_price shape consistent
        if (Object.prototype.hasOwnProperty.call(data, 'max_cpm_rate_local')) {
            data.bid_strategy_mode = 'fixed_bid_price';
            data.automatic_bid_price = processBidStrategyValue({ value: 'fixed_bid_price' });
        }

        return data;
    }

    // If 'target_device_os' is not used, return the 'presetData' early.
    if (isEmpty(originalTargetOS)) {
        return finalizePresetData(presetData);
    }

    // Helper function to set 'target_device_os' based on the provided options.
    function setTargetDeviceOS(deviceOsOptions) {
        const filteredOS = intersection(originalTargetOS, deviceOsOptions);
        if (isEmpty(filteredOS)) {
            delete presetData.target_device_os;
        } else {
            presetData.target_device_os = filteredOS;
        }
    }

    if (isCTVAd) {
        setTargetDeviceOS(CTV_DEVICE_OS_OPTIONS);
    } else if (isDOOHAd) {
        setTargetDeviceOS(DOOH_DEVICES_OS_OPTIONS);
    } else {
        presetData.target_device_os = difference(originalTargetOS, DOOH_AND_CTV_DEVICE_OS_OPTIONS);
        if (isEmpty(presetData.target_device_os)) {
            delete presetData.target_device_os;
        }
    }

    return finalizePresetData(presetData);
}

/**
 * Human-readable property names that survive platform/PG/env/bid-mode filtering and actually merge on apply.
 * Resolves labels via canonical key→name map (not stored `prop.name`) so stale Mongo names like
 * "Total Cost" surface as "Total Bid Price". Dedupes by canonical name (e.g. one "Carrier / ISP").
 * Pipeline: checked props → enabled fields → applied keys → unique names (no second gate pass).
 *
 * @param {string[]} platforms
 * @param {Object} adPreset
 * @param {Object} [options]
 * @param {boolean} [options.isGuaranteedInventory]
 * @param {string} [options.bidStrategyMode]
 * @param {Object} [options.environmentSettings] - Required when any checked field is env-gated
 * @returns {string[]}
 */
export function getAdPresetAppliedPropertyLabels(platforms, adPreset, options = {}) {
    const isGuaranteedInventory = Boolean(options.isGuaranteedInventory);
    const fieldGateOptions = {
        isGuaranteedInventory,
        ...(options.bidStrategyMode !== undefined
            ? { bidStrategyMode: options.bidStrategyMode }
            : {}),
        ...(options.environmentSettings !== undefined
            ? { environmentSettings: options.environmentSettings }
            : {}),
    };

    const enabledFields = getAdPresetEnabledFields(platforms, adPreset, fieldGateOptions);
    const appliedKeys = new Set(
        Object.keys(
            getAdPresetValuesToApply(platforms, adPreset, enabledFields, {
                allowSpecifyLocations: !isGuaranteedInventory,
            })
        )
    );

    const labels = [];
    const seenNames = new Set();

    for (const prop of getCheckedAdPresetProperties(adPreset)) {
        const applies = getAdPresetPropertyFields(prop).some(field => appliedKeys.has(field));
        const label = getAdPresetPropertyDisplayName(prop.key, prop.name);

        if (applies && !seenNames.has(label)) {
            seenNames.add(label);
            labels.push(label);
        }
    }

    return labels;
}
